import React, { useEffect, useMemo, useState } from "react";

import { DateRange } from "react-day-picker";

import dayjs from "dayjs";
import {
  ChevronDown,
  ChevronUp,
  Download,
  Filter,
  LayoutGrid,
  Loader2,
  Search,
  SlidersHorizontal,
  Table,
  X,
} from "lucide-react";

import { BatchTransportistaAssignmentDialog } from "~/components/BatchTransportistaAssignmentDialog";
import { LogisticaPorFacturarTable } from "~/components/LogisticaPorFacturarTable";
import { DateRangePicker } from "~/components/date-range-picker";
import { Badge } from "~/components/ui/badge";
import { Button } from "~/components/ui/button";
import { Card, CardContent } from "~/components/ui/card";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "~/components/ui/dropdown-menu";
import { Input } from "~/components/ui/input";
import { Label } from "~/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "~/components/ui/select";
import { Switch } from "~/components/ui/switch";
import { Tabs, TabsList, TabsTrigger } from "~/components/ui/tabs";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "~/components/ui/tooltip";
import { useAcceptedSalesNotes } from "~/hooks/useAcceptedSalesNotes";
import { useInfiniteScroll } from "~/hooks/useInfiniteScroll";
import { useSalesConditions } from "~/hooks/useSalesConditions";
import KanbanView from "~/pages/components/KanbanView";
import OrderDetailDialog from "~/pages/purchase-orders/OrderDetailDialog";
import {
  DashboardView,
  SalesTable,
} from "~/pages/purchase-orders/components/sales-notes";
import {
  processChartData,
  processStatusDistribution,
} from "~/pages/purchase-orders/components/sales-notes/helpers";
import { useOrderFilters } from "~/pages/purchase-orders/hooks/useOrderFilters";
import { useSalesRequests } from "~/pages/purchase-orders/hooks/useSalesRequests";
import {
  SalesData,
  StatusDistribution,
  TimePeriod,
  ViewMode,
  VisibleColumns,
} from "~/pages/purchase-orders/types/sales-notes";
import { exportSalesNotesToExcel } from "~/services/excelExportService";
import { exportAllSalesNotesToExcel } from "~/services/kanbanExportService";
import { useProfileStore } from "~/store/profileStore";
import { SalesRequest } from "~/types/client";

const SalesNotesPage = () => {
  // View state
  const [viewMode, setViewMode] = useState<ViewMode>("kanban");
  const [filtersVisible, setFiltersVisible] = useState(false);

  // Export loading state
  const [isExporting, setIsExporting] = useState(false);

  // Get user profile to check role
  // Use selector to avoid rerendering when unrelated store properties change
  const userRole = useProfileStore((state) => state.role);
  const isSeller = userRole === "seller";
  const isAdmin = userRole === "superadmin";
  const isTransportista = userRole === "transportista";
  const isLogistica = userRole === "logistica";
  const isCobranza = userRole === "cobranza";
  const isFacturacion = userRole === "facturacion";

  // Set viewAllSellers to true for roles that should see all sales notes in their visible columns
  const [viewAllSellers, setViewAllSellers] = useState(
    isAdmin || isLogistica || isCobranza || isFacturacion
  );

  // Track if filters have been manually applied (for roles that can see all sellers)
  const [filtersApplied, setFiltersApplied] = useState(false);

  // State for the selected order to view details
  const [selectedOrder, setSelectedOrder] = useState<SalesRequest | null>(null);

  // State for transportista assignment dialog
  const [isTransportistaDialogOpen, setIsTransportistaDialogOpen] =
    useState(false);
  const [selectedOrderIds, setSelectedOrderIds] = useState<string[]>([]);

  // Dashboard states
  const [salesData, setSalesData] = useState<SalesData[]>([]);
  const [statusDistribution, setStatusDistribution] = useState<
    StatusDistribution[]
  >([]);
  const [timePeriod, setTimePeriod] = useState<TimePeriod>("daily");

  // Helper function to get allowed statuses for additional frontend filtering
  // Note: Primary data scoping (sellers see only their data, transportistas see only assigned)
  // is handled at the database level in useFetchSalesRequests
  const getAllowedStatusesForRole = (role: string): string[] => {
    switch (role) {
      case "seller":
      case "superadmin":
      case "admin":
      case "risk":
        // These roles can see all workflow stages (database already handles seller data scoping)
        return [
          "pending_pricing", // Autorización Precio
          "pending", // Autorización Cobranza
          "prepaid", // Anticipado Pdte Pago
          "payment_to_check", // Validación Pago
          "accepted", // Aprobados Cobranza
          "por_facturar", // Por Facturar
          "in_transit", // Transito
          "delivered", // Entregado
          "rejected", // Rechazado
          "test", // Test
        ];

      case "cobranza":
        // Cobranza can see orders they need to process in the workflow
        return [
          "pending_pricing", // Autorización Precio
          "pending", // Autorización Cobranza
          "prepaid", // Anticipado Pdte Pago
          "payment_to_check", // Validación Pago
          "accepted", // Aprobados Cobranza
          "por_facturar", // Por Facturar
          "in_transit", // Transito
          "delivered", // Entregado
          "rejected", // Rechazado
          "test", // Test
        ];

      case "facturacion":
        // Facturación can see orders throughout the workflow to track progress
        return [
          "pending_pricing", // Autorización Precio
          "pending", // Autorización Cobranza
          "prepaid", // Anticipado Pdte Pago
          "payment_to_check", // Validación Pago
          "accepted", // Aprobados Cobranza
          "por_facturar", // Por Facturar
          "in_transit", // Transito
          "delivered", // Entregado
          "rejected", // Rechazado
          "test", // Test
        ];

      case "logistica":
        // Logística can see shipment-related statuses from "Por Facturar" onwards
        return [
          "por_facturar", // Por Facturar
          "in_transit", // En Tránsito
          "delivered", // Entregado
          "rejected", // Rechazado
          "test", // Test
        ];

      case "transportista":
        // Database already filters to assigned orders with in_transit/delivered
        // Transportistas should also see rejected orders to understand why they were rejected
        return [
          "in_transit", // Transito (their main responsibility)
          "delivered", // Entregado
          "rejected", // Rechazado - to see rejection reasons
        ];

      default:
        // Default: broader access
        return ["accepted", "por_facturar", "in_transit", "delivered"];
    }
  };

  // State for visible columns - all users can see all columns, just different statuses
  const [visibleColumns, setVisibleColumns] = useState<VisibleColumns>({
    id: true,
    status: true,
    client: true,
    seller: !isSeller && !isTransportista, // Hide seller column for both sellers and transportistas
    salesCondition: true,
    amount: true,
    items: false,
    date: true,
    paymentStatus: true,
    location: false,
    pricingWarning: true,
    facturado: true,
    rejectedReason: true, // Show rejected reason for all users including transportistas
  });

  // Fetch data from Supabase
  const {
    isLoading,
    isLoadingMore,
    hasMore,
    salesRequests,
    salesRequestItems,
    clientNames,
    clientInfo,
    clientRiskStatus,
    clientSalesConditions,
    paymentCounts,
    getSellerInfo,
    getApproverRejecterInfo,
    fetchSalesRequests,
    loadMoreSalesRequests,
    fetchItemsForSalesRequest,
    movedStatuses,
    registerMyStatusChange,
  } = useSalesRequests(viewAllSellers);

  // Get accepted client names for the dialog (accepted orders are fetched separately in KanbanView)
  const { acceptedClientNames, fetchAcceptedNotes } =
    useAcceptedSalesNotes(viewAllSellers);

  // Fetch accepted notes to populate client names for the dialog
  useEffect(() => {
    fetchAcceptedNotes();
  }, [fetchAcceptedNotes]);

  // Merge client names from main hook and accepted hook
  const mergedClientNames = React.useMemo(
    () => ({
      ...clientNames,
      ...acceptedClientNames,
    }),
    [clientNames, acceptedClientNames]
  );

  // Setup infinite scrolling
  useInfiniteScroll({
    hasMore,
    isLoading: isLoadingMore,
    loadMore: loadMoreSalesRequests,
    threshold: 300, // Load more when within 300px of bottom
  });

  // Check URL parameters on component mount to auto-open order detail
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const orderIdToOpen = urlParams.get("open");
    const guiaGenerated = urlParams.get("guia_generated");

    if (
      orderIdToOpen &&
      guiaGenerated === "true" &&
      salesRequests.length > 0 &&
      !isLoading
    ) {
      // Find the order in the sales requests
      const orderToOpen = salesRequests.find(
        (order) => order.id === orderIdToOpen
      );
      if (orderToOpen) {
        setSelectedOrder(orderToOpen);
        fetchItemsForSalesRequest(orderToOpen.id);
      }

      // Clean up URL parameters
      const newUrl = new URL(window.location.href);
      newUrl.searchParams.delete("open");
      newUrl.searchParams.delete("guia_generated");
      window.history.replaceState({}, "", newUrl.toString());
    }
  }, [salesRequests, fetchItemsForSalesRequest, isLoading]);

  // Set initial date range based on user role
  const getInitialDateRange = () => {
    if (isAdmin || isLogistica || isCobranza || isFacturacion) {
      // For roles that see all sales notes, start with a much wider range (1 year) to see more data
      return {
        from: new Date(new Date().setFullYear(new Date().getFullYear() - 1)),
        to: new Date(),
      };
    }
    // For other users, use a 90-day range
    return {
      from: new Date(new Date().setDate(new Date().getDate() - 90)),
      to: new Date(),
    };
  };

  // Filter orders
  const {
    searchQuery,
    setSearchQuery,
    dateRange,
    setDateRange,
    clients,
    selectedClientIds,
    setSelectedClientIds,
    sellers,
    selectedSellerIds,
    setSelectedSellerIds,
    filterByAmount,
    setFilterByAmount,
    sortOrder,
    setSortOrder,
    resetFilters,
    filterSummary,
    filteredOrders,
  } = useOrderFilters(salesRequests, clientNames, getInitialDateRange());

  const { getConditionById } = useSalesConditions();

  // Helper function to render sales condition
  const renderSalesCondition = (order: SalesRequest) => {
    const isInherited = !order.salesConditionId;
    const conditionId =
      order.salesConditionId || clientSalesConditions[order.clientId];
    const condition = conditionId ? getConditionById(conditionId) : null;

    if (!condition) return "-";

    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger className="cursor-help text-left">
            <span className={isInherited ? "italic text-gray-500" : ""}>
              {condition.name}
              {isInherited && " *"}
            </span>
          </TooltipTrigger>
          <TooltipContent>
            {isInherited
              ? "Heredada del cliente"
              : "Configurada específicamente para esta nota"}
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  };

  const handleOrderClick = (order: SalesRequest) => {
    setSelectedOrder(order);
    fetchItemsForSalesRequest(order.id);
  };

  const handleAssignToTransportista = (orderIds: string[]) => {
    setSelectedOrderIds(orderIds);
    setIsTransportistaDialogOpen(true);
  };

  const handleTransportistaAssignmentSuccess = () => {
    // Refresh the sales requests data
    fetchSalesRequests();
    setSelectedOrderIds([]);
  };

  // Get status options based on user role - only show statuses they can see
  const getStatusOptionsForRole = (role: string) => {
    const allowedStatuses = getAllowedStatusesForRole(role);
    const allStatusOptions = [
      { value: "pending_pricing", label: "Autorización por Precio" },
      { value: "pending", label: "Autorización Cobranza" },
      { value: "prepaid", label: "Anticipado Pendiente de Pago" },
      { value: "payment_to_check", label: "Validación Pago" },
      { value: "pending_credit", label: "Crédito Insuficiente" },
      { value: "accepted", label: "Aprobada" },
      { value: "por_facturar", label: "Por Facturar" },
      { value: "in_transit", label: "En tránsito" },
      { value: "delivered", label: "Entregada" },
      { value: "rejected", label: "Rechazada" },
      { value: "test", label: "Test" },
    ];

    const filteredOptions = allStatusOptions.filter((option) =>
      allowedStatuses.includes(option.value)
    );

    return [{ value: "all", label: "Todos los estados" }, ...filteredOptions];
  };

  const statusOptions = getStatusOptionsForRole(userRole || "");

  // Payment status options
  const paymentOptions = [
    { value: "all", label: "Todos" },
    { value: "paid", label: "Pagado" },
    { value: "unpaid", label: "No pagado" },
  ];

  // State for status and payment filters
  const [statusFilter, setStatusFilter] = useState("all");
  const [paymentFilter, setPaymentFilter] = useState("all");
  const [pricingWarningFilter, setPricingWarningFilter] = useState("all");

  // Function to check if any filters are actively being applied beyond defaults
  const checkIfAnyFiltersActive = () => {
    const hasActiveStatusFilter = statusFilter !== "all";
    const hasActivePaymentFilter = paymentFilter !== "all";
    const hasActiveSearchFilter = searchQuery.trim() !== "";

    // Check if date range is different from the default (last 30 days)
    const defaultFromDate = new Date(
      new Date().setDate(new Date().getDate() - 30)
    );
    const defaultToDate = new Date();
    const isDefaultDateRange =
      dateRange?.from?.toDateString() === defaultFromDate.toDateString() &&
      dateRange?.to?.toDateString() === defaultToDate.toDateString();
    const hasActiveDateFilter =
      !!(dateRange?.from || dateRange?.to) && !isDefaultDateRange;

    const hasActiveAmountFilter = !!(
      filterByAmount?.min || filterByAmount?.max
    );

    return (
      hasActiveStatusFilter ||
      hasActivePaymentFilter ||
      hasActiveSearchFilter ||
      hasActiveDateFilter ||
      hasActiveAmountFilter
    );
  };

  // Handle status filter change
  const handleStatusFilterChange = (value: string) => {
    setStatusFilter(value);

    // Check if any filters are active after this change
    const hasActiveStatusFilter = value !== "all";
    const hasOtherActiveFilters =
      paymentFilter !== "all" ||
      pricingWarningFilter !== "all" ||
      searchQuery.trim() !== "" ||
      !!(filterByAmount?.min || filterByAmount?.max);

    // Check if date range is different from default
    const defaultFromDate = new Date(
      new Date().setDate(new Date().getDate() - 30)
    );
    const defaultToDate = new Date();
    const isDefaultDateRange =
      dateRange?.from?.toDateString() === defaultFromDate.toDateString() &&
      dateRange?.to?.toDateString() === defaultToDate.toDateString();
    const hasActiveDateFilter =
      !!(dateRange?.from || dateRange?.to) && !isDefaultDateRange;

    setFiltersApplied(
      hasActiveStatusFilter || hasOtherActiveFilters || hasActiveDateFilter
    );
  };

  // Handle payment filter change
  const handlePaymentFilterChange = (value: string) => {
    setPaymentFilter(value);

    // Check if any filters are active after this change
    const hasActivePaymentFilter = value !== "all";
    const hasOtherActiveFilters =
      statusFilter !== "all" ||
      pricingWarningFilter !== "all" ||
      searchQuery.trim() !== "" ||
      !!(filterByAmount?.min || filterByAmount?.max);

    // Check if date range is different from default
    const defaultFromDate = new Date(
      new Date().setDate(new Date().getDate() - 30)
    );
    const defaultToDate = new Date();
    const isDefaultDateRange =
      dateRange?.from?.toDateString() === defaultFromDate.toDateString() &&
      dateRange?.to?.toDateString() === defaultToDate.toDateString();
    const hasActiveDateFilter =
      !!(dateRange?.from || dateRange?.to) && !isDefaultDateRange;

    setFiltersApplied(
      hasActivePaymentFilter || hasOtherActiveFilters || hasActiveDateFilter
    );
  };

  // Handle pricing warning filter change
  const handlePricingWarningFilterChange = (value: string) => {
    setPricingWarningFilter(value);

    // Check if any filters are active after this change
    const hasActivePricingFilter = value !== "all";
    const hasOtherActiveFilters =
      statusFilter !== "all" ||
      paymentFilter !== "all" ||
      searchQuery.trim() !== "" ||
      !!(filterByAmount?.min || filterByAmount?.max);

    // Check if date range is different from default
    const defaultFromDate = new Date(
      new Date().setDate(new Date().getDate() - 30)
    );
    const defaultToDate = new Date();
    const isDefaultDateRange =
      dateRange?.from?.toDateString() === defaultFromDate.toDateString() &&
      dateRange?.to?.toDateString() === defaultToDate.toDateString();
    const hasActiveDateFilter =
      !!(dateRange?.from || dateRange?.to) && !isDefaultDateRange;

    setFiltersApplied(
      hasActivePricingFilter || hasOtherActiveFilters || hasActiveDateFilter
    );
  };

  // Handle search query change
  const handleSearchQueryChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newSearchQuery = e.target.value;
    setSearchQuery(newSearchQuery);

    // Check if any filters are active after this change
    const hasActiveSearchFilter = newSearchQuery.trim() !== "";
    const hasOtherActiveFilters =
      statusFilter !== "all" ||
      paymentFilter !== "all" ||
      pricingWarningFilter !== "all" ||
      !!(filterByAmount?.min || filterByAmount?.max);

    // Check if date range is different from default
    const defaultFromDate = new Date(
      new Date().setDate(new Date().getDate() - 30)
    );
    const defaultToDate = new Date();
    const isDefaultDateRange =
      dateRange?.from?.toDateString() === defaultFromDate.toDateString() &&
      dateRange?.to?.toDateString() === defaultToDate.toDateString();
    const hasActiveDateFilter =
      !!(dateRange?.from || dateRange?.to) && !isDefaultDateRange;

    setFiltersApplied(
      hasActiveSearchFilter || hasOtherActiveFilters || hasActiveDateFilter
    );
  };

  // Handle date range change
  const handleDateRangeChange = (range: DateRange | undefined) => {
    if (!range) return;
    setDateRange(range);

    // Check if the new date range is different from default
    const defaultFromDate = new Date(
      new Date().setDate(new Date().getDate() - 30)
    );
    const defaultToDate = new Date();
    const isDefaultDateRange =
      range?.from?.toDateString() === defaultFromDate.toDateString() &&
      range?.to?.toDateString() === defaultToDate.toDateString();
    const hasActiveDateFilter =
      !!(range?.from || range?.to) && !isDefaultDateRange;

    // Check if any other filters are active
    const hasOtherActiveFilters =
      statusFilter !== "all" ||
      paymentFilter !== "all" ||
      pricingWarningFilter !== "all" ||
      searchQuery.trim() !== "" ||
      !!(filterByAmount?.min || filterByAmount?.max);

    setFiltersApplied(hasActiveDateFilter || hasOtherActiveFilters);
  };

  // Handle amount filter changes
  const handleMinAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const min = parseInt(e.target.value) || 0;
    setFilterByAmount({
      min,
      max: filterByAmount?.max || null,
    });

    // Set filtersApplied if any filters are still active after this change
    setTimeout(() => {
      setFiltersApplied(checkIfAnyFiltersActive());
    }, 0);
  };

  // Handle max amount change
  const handleMaxAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const max = e.target.value ? parseInt(e.target.value) : null;
    setFilterByAmount({
      min: filterByAmount?.min || 0,
      max,
    });

    // Set filtersApplied if any filters are still active after this change
    setTimeout(() => {
      setFiltersApplied(checkIfAnyFiltersActive());
    }, 0);
  };

  // Export data as Excel - fetches all data from database (like per-column export)
  const exportAllWithColumnFormat = async () => {
    setIsExporting(true);
    try {
      const { id: userId } = useProfileStore.getState();
      await exportAllSalesNotesToExcel({
        dateRange,
        viewAllSellers,
        userRole,
        userId,
        excludeStatuses: ["delivered", "rejected", "test"],
      });
    } catch (error) {
      console.error("Error exporting to Excel:", error);
    } finally {
      setIsExporting(false);
    }
  };

  // Helper to get the appropriate empty state message based on role
  const getEmptyStateMessage = () => {
    if (userRole === "seller") {
      return "No tienes notas de venta creadas. Solo puedes ver las notas de venta que has creado.";
    }
    if (userRole === "transportista") {
      return "No tienes transportes asignados. Solo puedes ver notas de venta que te han sido asignadas para transporte.";
    }
    if (userRole === "cobranza") {
      return "No hay notas de venta pendientes. Puedes ver notas que requieren autorización o validación de pagos.";
    }
    if (userRole === "facturacion") {
      return "No hay notas listas para facturar. Puedes ver notas aprobadas y procesadas.";
    }
    if (userRole === "logistica") {
      return "No hay entregas pendientes. Puedes ver notas aprobadas y en proceso de entrega.";
    }
    return "No se encontraron notas de venta para los criterios seleccionados.";
  };

  // Handle time period change for charts
  const handleTimePeriodChange = (newPeriod: TimePeriod) => {
    setTimePeriod(newPeriod);
    setSalesData(processChartData(displayedOrders, newPeriod));
  };

  // Create a wrapper for resetFilters
  const handleResetFilters = () => {
    resetFilters();
    setStatusFilter("all");
    setPaymentFilter("all");
    setPricingWarningFilter("all");
    setFiltersApplied(false);

    // For roles that see all sales notes, force a fetch to ensure we get all data
    if (isAdmin || isLogistica || isCobranza || isFacturacion) {
      setTimeout(() => {
        fetchSalesRequests();
      }, 0);
    }
  };

  // Filter orders by status and payment
  // Note: Primary data access control is handled at database level:
  // - Sellers only get their own sales requests (created_by = user_id)
  // - Transportistas only get assigned requests (assigned_transportista = user_id)
  // - Other roles get appropriate requests based on workflow stage
  const getFilteredOrdersByStatus = () => {
    // Wait until data is loaded before filtering
    if (isLoading || !salesRequests) {
      return [];
    }

    // Always start with the properly filtered orders from useOrderFilters hook
    // This ensures that date filters and other filters are always applied
    let filtered = filteredOrders?.length ? [...filteredOrders] : [];

    // Apply role-based workflow stage filtering for UI consistency
    const allowedStatuses = getAllowedStatusesForRole(userRole || "");
    filtered = filtered.filter((order) =>
      allowedStatuses.includes(order.status)
    );

    // Apply additional status filter
    if (statusFilter !== "all") {
      filtered = filtered.filter((order) => order.status === statusFilter);
    }

    // Apply additional payment filter
    if (paymentFilter !== "all") {
      filtered = filtered.filter((order) =>
        paymentFilter === "paid" ? order.paid : !order.paid
      );
    }

    // Apply pricing warning filter
    if (pricingWarningFilter !== "all") {
      filtered = filtered.filter((order) =>
        pricingWarningFilter === "warning"
          ? order.hasPricingWarning
          : !order.hasPricingWarning
      );
    }

    return filtered;
  };

  // Memoize displayedOrders to prevent new array reference on every render
  // This fixes infinite rerenders caused by useEffect with displayedOrders in deps
  const displayedOrders = useMemo(() => {
    if (isLoading) return [];
    return getFilteredOrdersByStatus();
  }, [
    isLoading,
    salesRequests,
    filteredOrders,
    statusFilter,
    paymentFilter,
    pricingWarningFilter,
    userRole,
  ]);

  // Calculate totals for KPIs
  const totalOrders = displayedOrders.length;
  const totalAmount = displayedOrders.reduce(
    (sum, order) => sum + order.totalAmount,
    0
  );
  const pendingAmount = displayedOrders
    .filter((order) => !order.paid)
    .reduce((sum, order) => sum + order.totalAmount, 0);
  const approvalRate =
    totalOrders > 0
      ? Math.round(
          (displayedOrders.filter((order) => order.status === "accepted")
            .length /
            totalOrders) *
            100
        )
      : 0;

  // Process dashboard chart data
  useEffect(() => {
    setSalesData(processChartData(displayedOrders, timePeriod));
    setStatusDistribution(processStatusDistribution(displayedOrders));
  }, [displayedOrders, timePeriod]);

  // Re-fetch data when viewAllSellers changes
  useEffect(() => {
    fetchSalesRequests();
  }, [viewAllSellers]);

  return (
    <div className="w-full min-w-0">
      {/* Page Header with Title and Description */}
      <div className="flex flex-col gap-4 pb-2 xl:flex-row xl:items-center xl:justify-between">
        <div className="flex flex-col gap-2">
          <h2 className="text-2xl font-bold tracking-tight">
            {userRole === "logistica"
              ? "Envíos Pendientes"
              : userRole === "transportista"
                ? "Mis Transportes"
                : "Notas de Venta"}
          </h2>
          <p className="text-muted-foreground">
            {userRole === "logistica"
              ? "Gestión de envíos pendientes"
              : userRole === "transportista"
                ? "Gestión de transportes asignados"
                : "Gestión de notas de venta y seguimiento de pagos"}
          </p>
        </div>

        {/* Main Actions */}
        <div className="flex flex-col gap-2 lg:flex-row">
          {/* View toggle */}
          <Tabs
            value={viewMode}
            onValueChange={(value) => setViewMode(value as ViewMode)}
            className="h-9 w-full rounded-md border lg:w-auto"
          >
            <TabsList className="grid h-9 w-full grid-cols-2 bg-transparent lg:w-[240px]">
              <TabsTrigger
                value="table"
                className="flex items-center justify-center gap-2 px-4 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground"
              >
                <Table className="h-4 w-4" />
                <span>Tabla</span>
              </TabsTrigger>
              <TabsTrigger
                value="kanban"
                className="flex items-center justify-center gap-2 px-4 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground"
              >
                <LayoutGrid className="h-4 w-4" />
                <span>Kanban</span>
              </TabsTrigger>
            </TabsList>
          </Tabs>

          {/* Export button */}
          <Button
            variant="outline"
            size="sm"
            onClick={exportAllWithColumnFormat}
            disabled={isExporting}
          >
            {isExporting ? (
              <>
                <Loader2 className="mr-1 h-4 w-4 animate-spin" />
                <span>Exportando...</span>
              </>
            ) : (
              <>
                <Download className="mr-1 h-4 w-4" />
                <span>Exportar Excel</span>
              </>
            )}
          </Button>

          {/* Columns dropdown */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm">
                <SlidersHorizontal className="mr-1 h-4 w-4" />
                <span>Columnas</span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuLabel>Mostrar columnas</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuCheckboxItem
                checked={visibleColumns.id}
                onCheckedChange={(checked) =>
                  setVisibleColumns({ ...visibleColumns, id: checked })
                }
              >
                ID
              </DropdownMenuCheckboxItem>
              <DropdownMenuCheckboxItem
                checked={visibleColumns.status}
                onCheckedChange={(checked) =>
                  setVisibleColumns({ ...visibleColumns, status: checked })
                }
              >
                Estado
              </DropdownMenuCheckboxItem>
              <DropdownMenuCheckboxItem
                checked={visibleColumns.client}
                onCheckedChange={(checked) =>
                  setVisibleColumns({ ...visibleColumns, client: checked })
                }
              >
                Cliente
              </DropdownMenuCheckboxItem>
              {!isSeller && !isTransportista && (
                <DropdownMenuCheckboxItem
                  checked={visibleColumns.seller}
                  onCheckedChange={(checked) =>
                    setVisibleColumns({ ...visibleColumns, seller: checked })
                  }
                >
                  Vendedor
                </DropdownMenuCheckboxItem>
              )}
              <DropdownMenuCheckboxItem
                checked={visibleColumns.salesCondition}
                onCheckedChange={(checked) =>
                  setVisibleColumns({
                    ...visibleColumns,
                    salesCondition: checked,
                  })
                }
              >
                Condición de Venta
              </DropdownMenuCheckboxItem>
              <DropdownMenuCheckboxItem
                checked={visibleColumns.amount}
                onCheckedChange={(checked) =>
                  setVisibleColumns({ ...visibleColumns, amount: checked })
                }
              >
                Monto
              </DropdownMenuCheckboxItem>
              <DropdownMenuCheckboxItem
                checked={visibleColumns.items}
                onCheckedChange={(checked) =>
                  setVisibleColumns({ ...visibleColumns, items: checked })
                }
              >
                Productos
              </DropdownMenuCheckboxItem>
              <DropdownMenuCheckboxItem
                checked={visibleColumns.date}
                onCheckedChange={(checked) =>
                  setVisibleColumns({ ...visibleColumns, date: checked })
                }
              >
                Fecha
              </DropdownMenuCheckboxItem>
              <DropdownMenuCheckboxItem
                checked={visibleColumns.paymentStatus}
                onCheckedChange={(checked) =>
                  setVisibleColumns({
                    ...visibleColumns,
                    paymentStatus: checked,
                  })
                }
              >
                Estado de Pago
              </DropdownMenuCheckboxItem>
              <DropdownMenuCheckboxItem
                checked={visibleColumns.location}
                onCheckedChange={(checked) =>
                  setVisibleColumns({ ...visibleColumns, location: checked })
                }
              >
                Ubicación
              </DropdownMenuCheckboxItem>
              <DropdownMenuCheckboxItem
                checked={visibleColumns.pricingWarning}
                onCheckedChange={(checked) =>
                  setVisibleColumns({
                    ...visibleColumns,
                    pricingWarning: checked,
                  })
                }
              >
                Alerta de Precio
              </DropdownMenuCheckboxItem>
              <DropdownMenuCheckboxItem
                checked={visibleColumns.facturado}
                onCheckedChange={(checked) =>
                  setVisibleColumns({
                    ...visibleColumns,
                    facturado: checked,
                  })
                }
              >
                Facturado
              </DropdownMenuCheckboxItem>
              <DropdownMenuCheckboxItem
                checked={visibleColumns.rejectedReason}
                onCheckedChange={(checked) =>
                  setVisibleColumns({
                    ...visibleColumns,
                    rejectedReason: checked,
                  })
                }
              >
                Motivo de Rechazo
              </DropdownMenuCheckboxItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Filters and Search Bar Row */}
      <div className="flex w-full flex-col gap-4">
        {/* Top row with search and view toggle */}
        <div className="flex flex-row justify-between gap-3">
          {/* Search input */}
          <div className="relative w-full sm:max-w-md">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 transform text-muted-foreground" />
            <Input
              placeholder="Buscar por ID, cliente o factura..."
              value={searchQuery}
              onChange={handleSearchQueryChange}
              className="h-10 w-full pl-9"
            />
          </div>

          {/* Admin view all sellers toggle */}
          {isAdmin && (
            <div className="flex w-full items-center space-x-2 sm:w-auto">
              <Switch
                id="view-all-sellers"
                checked={viewAllSellers}
                onCheckedChange={setViewAllSellers}
              />
              <Label
                htmlFor="view-all-sellers"
                className="whitespace-nowrap text-sm"
              >
                Ver todos
              </Label>
            </div>
          )}
        </div>

        {/* Bottom row with filters */}
        <div className="grid grid-cols-2 gap-2 xl:flex">
          {/* Status filter */}
          <Select value={statusFilter} onValueChange={handleStatusFilterChange}>
            <SelectTrigger className="h-10 w-full xl:w-[140px]">
              <SelectValue placeholder="Estado" />
            </SelectTrigger>
            <SelectContent>
              {statusOptions.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {/* Payment filter */}
          <Select
            value={paymentFilter}
            onValueChange={handlePaymentFilterChange}
          >
            <SelectTrigger className="h-10 w-full xl:w-[140px]">
              <SelectValue placeholder="Pago" />
            </SelectTrigger>
            <SelectContent>
              {paymentOptions.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {/* Date Range Picker */}
          <DateRangePicker
            dateRange={dateRange}
            onDateRangeChange={handleDateRangeChange}
            className="h-10 w-full xl:w-auto"
          />

          {/* Action buttons wrapper - full width on smaller screens */}
          <div className="col-span-2 flex gap-2 xl:col-span-1 xl:ml-auto">
            <Button
              variant="outline"
              size="default"
              onClick={() => setFiltersVisible(!filtersVisible)}
              className="h-10 flex-1 xl:flex-initial"
            >
              <Filter className="mr-2 h-4 w-4" />
              <span>Más filtros</span>
              {filtersVisible ? (
                <ChevronUp className="ml-2 h-4 w-4" />
              ) : (
                <ChevronDown className="ml-2 h-4 w-4" />
              )}
            </Button>

            {/* Clear filters button */}
            {filterSummary.length > 0 && (
              <Button
                variant="ghost"
                size="default"
                onClick={handleResetFilters}
                className="h-10 flex-1 text-muted-foreground xl:flex-initial"
              >
                <X className="mr-2 h-4 w-4" />
                <span>Limpiar</span>
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Active Filters Display */}
      {filterSummary.length > 0 && (
        <div className="my-4 flex flex-wrap items-center gap-1">
          <span className="text-xs text-muted-foreground">
            Filtros activos:
          </span>
          {filterSummary.map((filter, index) => (
            <Badge key={index} variant="outline" className="px-2 py-0 text-xs">
              {filter}
            </Badge>
          ))}
        </div>
      )}

      {/* Advanced filters panel */}
      {filtersVisible && (
        <Card className="mb-4 shadow-sm">
          <CardContent className="grid grid-cols-1 gap-4 p-4 sm:grid-cols-3">
            {/* Amount Range */}
            <div className="space-y-2">
              <Label>Rango de Monto</Label>
              <div className="flex items-center gap-2">
                <Input
                  type="number"
                  placeholder="Mínimo"
                  value={filterByAmount?.min || ""}
                  onChange={handleMinAmountChange}
                  className="h-9"
                />
                <span>-</span>
                <Input
                  type="number"
                  placeholder="Máximo"
                  value={filterByAmount?.max || ""}
                  onChange={handleMaxAmountChange}
                  className="h-9"
                />
              </div>
            </div>

            {/* Sort Options */}
            <div className="space-y-2">
              <Label>Ordenar por</Label>
              <div className="grid grid-cols-2 gap-2">
                <Select
                  value={sortOrder.field as string}
                  onValueChange={(value) =>
                    setSortOrder({
                      ...sortOrder,
                      field: value as keyof SalesRequest,
                    })
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Campo" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="createdAt">Fecha</SelectItem>
                    <SelectItem value="totalAmount">Monto</SelectItem>
                    <SelectItem value="status">Estado</SelectItem>
                  </SelectContent>
                </Select>
                <Select
                  value={sortOrder.direction}
                  onValueChange={(value) =>
                    setSortOrder({
                      ...sortOrder,
                      direction: value as "asc" | "desc",
                    })
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Orden" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="asc">Ascendente</SelectItem>
                    <SelectItem value="desc">Descendente</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Admin controls or other advanced filters can go here */}
            <div className="space-y-2">
              <Label>Alerta de Precio</Label>
              <Select
                value={pricingWarningFilter}
                onValueChange={handlePricingWarningFilterChange}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Advertencia" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  <SelectItem value="warning">Con Advertencia</SelectItem>
                  <SelectItem value="no_warning">Sin Advertencia</SelectItem>
                </SelectContent>
              </Select>
              {isAdmin && (
                <div className="flex items-center space-x-2 sm:hidden">
                  <Switch
                    id="mobile-view-all-sellers"
                    checked={viewAllSellers}
                    onCheckedChange={setViewAllSellers}
                  />
                  <Label htmlFor="mobile-view-all-sellers">
                    Ver todos los vendedores
                  </Label>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Dashboard View */}
      {viewMode === "dashboard" && (
        <DashboardView
          displayedOrders={displayedOrders}
          totalOrders={totalOrders}
          totalAmount={totalAmount}
          pendingAmount={pendingAmount}
          approvalRate={approvalRate}
          salesData={salesData}
          statusDistribution={statusDistribution}
          timePeriod={timePeriod}
          handleTimePeriodChange={handleTimePeriodChange}
          clientNames={clientNames}
          getSellerInfo={getSellerInfo}
          handleOrderClick={handleOrderClick}
          isSeller={isSeller}
          setViewMode={setViewMode}
          getEmptyStateMessage={getEmptyStateMessage}
        />
      )}

      {/* Table View */}
      {viewMode === "table" && (
        <SalesTable
          displayedOrders={displayedOrders}
          isLoading={isLoading}
          getEmptyStateMessage={getEmptyStateMessage}
          visibleColumns={visibleColumns}
          clientNames={clientNames}
          clientInfo={clientInfo}
          getSellerInfo={getSellerInfo}
          renderSalesCondition={renderSalesCondition}
          salesRequestItems={salesRequestItems}
          handleOrderClick={handleOrderClick}
          isSeller={isSeller}
        />
      )}

      {/* Kanban View */}
      {viewMode === "kanban" && (
        <div className="space-y-6">
          <Card>
            <CardContent className="overflow-hidden p-0 md:p-4">
              <KanbanView
                salesRequests={displayedOrders}
                isLoading={isLoading}
                clientNames={clientNames}
                clientInfo={clientInfo}
                salesRequestItems={salesRequestItems}
                onOrderClick={handleOrderClick}
                getSellerInfo={getSellerInfo}
                paymentCounts={paymentCounts}
                clientSalesConditions={clientSalesConditions}
                dateRange={dateRange}
                viewAllSellers={viewAllSellers}
                movedStatuses={movedStatuses}
              />
            </CardContent>
          </Card>

          {/* Logistica-specific table for Por Facturar orders */}
          {(isLogistica || isAdmin) && (
            <LogisticaPorFacturarTable
              salesRequests={displayedOrders}
              clientNames={clientNames}
              salesRequestItems={salesRequestItems}
              getSellerInfo={getSellerInfo}
              onOrderClick={handleOrderClick}
              fetchItemsForSalesRequest={async (salesRequestId: string) => {
                await fetchItemsForSalesRequest(salesRequestId);
              }}
              onAssignToTransportista={handleAssignToTransportista}
            />
          )}
        </div>
      )}

      {/* Infinite Scroll Loading Indicator */}
      {isLoadingMore && (
        <div className="flex justify-center py-8">
          <div className="flex items-center space-x-2 text-muted-foreground">
            <Loader2 className="h-5 w-5 animate-spin" />
            <span>Cargando más notas de venta...</span>
          </div>
        </div>
      )}

      {/* Order Detail Dialog */}
      {selectedOrder && (
        <OrderDetailDialog
          selectedOrder={selectedOrder}
          setSelectedOrder={setSelectedOrder}
          getSellerInfo={getSellerInfo}
          getApproverRejecterInfo={getApproverRejecterInfo}
          clientNames={mergedClientNames}
          items={salesRequestItems[selectedOrder.id] || []}
          onOrderStatusChange={() => {
            if (selectedOrder) registerMyStatusChange(selectedOrder.id);
            fetchSalesRequests(true);
          }}
          clientRiskStatus={clientRiskStatus}
          clientInfo={clientInfo}
        />
      )}

      {/* Batch Transportista Assignment Dialog */}
      <BatchTransportistaAssignmentDialog
        open={isTransportistaDialogOpen}
        onOpenChange={setIsTransportistaDialogOpen}
        selectedOrderIds={selectedOrderIds}
        onSuccess={handleTransportistaAssignmentSuccess}
      />
    </div>
  );
};

export default SalesNotesPage;
